import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  listAttempts,
  getAttemptDetail,
  AttemptHistoryEntry,
  AttemptDetail,
  QuestionReplay,
} from '../services/studentScheduledTestService';

export default function StudentAttemptHistoryPage() {
  const { courseId, testId } = useParams<{ courseId: string; testId: string }>();
  const cId = Number(courseId);
  const tId = Number(testId);
  const navigate = useNavigate();

  const [attempts, setAttempts] = useState<AttemptHistoryEntry[]>([]);
  const [selected, setSelected] = useState<AttemptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [replayLoading, setReplayLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAttempts(cId, tId)
      .then(setAttempts)
      .catch(() => setError('Failed to load attempts'))
      .finally(() => setLoading(false));
  }, [cId, tId]);

  const loadReplay = async (attemptId: number) => {
    setReplayLoading(true);
    try {
      const detail = await getAttemptDetail(cId, tId, attemptId);
      setSelected(detail);
    } catch {
      setError('Failed to load attempt detail');
    } finally {
      setReplayLoading(false);
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 8, display: 'block', mx: 'auto' }} />;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          if (selected) {
            setSelected(null);
          } else {
            navigate(-1);
          }
        }}
        sx={{ mb: 2 }}
      >
        {selected ? 'Back to History' : 'Back to Tests'}
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!selected ? (
        <>
          <Typography variant="h5" gutterBottom>Attempt History</Typography>
          {attempts.length === 0 ? (
            <Typography color="text.secondary">No attempts yet. Take the test to see your results here.</Typography>
          ) : (
            <List>
              {attempts.map((a) => {
                const pct = a.scorePercent ?? 0;
                const color = pct >= 70 ? 'success' : pct >= 50 ? 'warning' : 'error';
                return (
                  <React.Fragment key={a.attemptId}>
                    <ListItem
                      button
                      onClick={() => loadReplay(a.attemptId)}
                      sx={{ py: 1.5 }}
                    >
                      <ListItemText
                        primary={`Attempt #${a.attemptNumber}`}
                        secondary={new Date(a.submittedAt).toLocaleString()}
                      />
                      <Chip
                        label={`${a.scoreCorrect}/${a.scoreTotal} (${pct}%)`}
                        color={color as any}
                        variant="outlined"
                        size="small"
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </>
      ) : replayLoading ? (
        <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />
      ) : (
        <AttemptReplay detail={selected} />
      )}
    </Container>
  );
}

function AttemptReplay({ detail }: { detail: AttemptDetail }) {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>Attempt Review</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Score: {detail.scoreCorrect}/{detail.scoreTotal} · {new Date(detail.submittedAt).toLocaleString()}
      </Typography>

      {detail.questions.map((q: QuestionReplay, idx: number) => (
        <Box
          key={q.snapshotQuestionId}
          sx={{
            mb: 3,
            p: 2,
            border: 1,
            borderColor: q.isCorrect ? 'success.light' : 'error.light',
            borderRadius: 1,
            bgcolor: q.isCorrect ? 'success.50' : 'error.50',
          }}
        >
          <Typography fontWeight={600} gutterBottom>
            {idx + 1}. {q.questionText}
          </Typography>
          <List dense disablePadding>
            {q.options.map((opt) => {
              const isSelected = opt.index === q.selectedOptionIndex;
              return (
                <ListItem key={opt.index} sx={{ py: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: opt.isCorrect ? 'success.main' : isSelected ? 'error.main' : 'text.primary',
                      fontWeight: (opt.isCorrect || isSelected) ? 600 : 400,
                    }}
                  >
                    {isSelected ? '→ ' : '   '}{opt.text}
                    {opt.isCorrect ? ' ✓' : ''}
                  </Typography>
                </ListItem>
              );
            })}
          </List>
          {q.explanation && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {q.explanation}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
